import React, { useEffect, useMemo, useState } from "react";
import { useQuickEditRefresh } from "@/hooks/useQuickEditRefresh";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, PoolPill } from "@/components/ui-extra";
import { BaseAssignControl } from "@/components/BaseAssignControl";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ChevronLeft, ChevronRight, Sparkles, Heart, Loader2, Pencil, Camera, Trash2, AlertTriangle, DollarSign, Tag, Send, EyeOff, CheckCircle2, FileDown, Share2, Stethoscope, TrendingUp, Users, Star, CalendarRange, Video, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from "recharts";
import { safeFormatDate, safeDistanceToNow, toDate } from "@/lib/dates";
import { stripLegacyLabel } from "@/lib/legacy-label";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency } from "@/lib/currency";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ShareHorseDialog } from "@/components/ShareHorseDialog";
import { Switch } from "@/components/ui/switch";
import { HorseAgeBadge } from "@/components/HorseAgeBadge";
import { poloAge } from "@/lib/polo-age";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const RATING_LABELS = {
  stride_comfort: "Stride comfort", balance: "Balance overall", balance_left: "Balance L",
  balance_right: "Balance R", agility: "Agility", temperament: "Temperament",
  top_speed: "Top speed", acceleration: "Acceleration", rideoff_strength: "Ride-off strength",
  stopping: "Stopping", lead_changes: "Lead changes", mouth_rein: "Mouth & rein",
  env_reaction: "Env reaction", recovery: "Recovery", overall: "Overall suitability",
};

export default function HorseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isExecutive, isMaster, hasPerm } = useAuth();
  const canSeePrice = isAdmin || isExecutive || isMaster;
  const canPublishPrice = isMaster || hasPerm?.("horses:publish_price");
  // Deletion is deliberately admin-only in the default role catalog. The
  // `horses:delete` permission key is granted by the "admin" system role
  // and the "master_admin" role bypasses all checks. Executive does NOT
  // get delete by default (destructive, admin-scope only).
  const canDeleteHorse = isMaster || hasPerm?.("horses:delete");
  // Per-horse ranking detail PDF (same generator as the leaderboard).
  const canDownloadReport = isMaster || hasPerm?.("rankings:download_horse_report");
  const [reporting, setReporting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [horse, setHorse] = useState(null);
  const [chukkas, setChukkas] = useState([]);
  const [health, setHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  // Prev/next neighbour horses (alphabetical) — powers the "scroll
  // through horses" buttons in the header. Fetched in parallel with
  // the main horse load so switching horses feels instant. Falls
  // back to disabled buttons on the ends of the alphabet.
  const [neighbors, setNeighbors] = useState({ prev: null, next: null, position: null, total: null });
  // Videos live at the parent so the management panel (upload/delete) and
  // the hero photo panel's full-screen viewer share one source of truth —
  // adding a clip below shows up instantly in the gallery viewer above.
  const [videos, setVideos] = useState(null);
  const loadVideos = React.useCallback(async () => {
    try { const r = await api.get(`/horses/${id}/videos`); setVideos(r.data?.videos || []); }
    catch (_e) { setVideos([]); }
  }, [id]);
  useEffect(() => { loadVideos(); }, [loadVideos]);

  const load = async () => {
    setLoading(true);
    try {
      const [h, c, hl, n] = await Promise.all([
        api.get(`/horses/${id}`),
        // Ask for a generous batch so the Rating-notes card can show
        // every rider writeup for veteran horses (backend defaults to
        // 50). We still cap at 500 to avoid pathological cases where
        // a stale horse has accumulated thousands of legacy rows.
        api.get(`/horses/${id}/chukkas`, { params: { limit: 500 } }),
        api.get(`/horses/${id}/health`),
        // Neighbors endpoint tolerates 404 (horse deleted underneath
        // us) — treat that as "no neighbors" so the page can still
        // render an error state without an unhandled promise reject.
        api.get(`/horses/${id}/neighbors`).catch(() => ({ data: { prev: null, next: null, position: null, total: null } })),
      ]);
      setHorse(h.data); setChukkas(c.data); setHealth(hl.data);
      setNeighbors(n.data || { prev: null, next: null });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useQuickEditRefresh(load, [id]);

  const downloadReport = async () => {
    if (!horse || reporting) return;
    setReporting(true);
    try {
      const [rankRes, photosRes] = await Promise.all([
        api.get("/horses/rankings", { params: { limit: 500 } }).catch(() => ({ data: [] })),
        api.get(`/horses/${horse.id}/photos`).catch(() => ({ data: { photos: [] } })),
      ]);
      const ranked = rankRes.data || [];
      const idx = ranked.findIndex((x) => x.id === horse.id);
      const { downloadHorseReport } = await import("@/lib/pdfHorseReport");
      await downloadHorseReport({
        horse,
        rank: idx >= 0 ? idx + 1 : undefined,
        total: ranked.length || undefined,
        chukkas,
        photos: photosRes.data?.photos || [],
        canSeeHorseDetails: true,
      });
      toast.success(`Report ready for ${horse.name}`);
    } catch (e) {
      toast.error("Could not generate the horse report");
      console.error(e);
    } finally {
      setReporting(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!horse) return <div className="text-sm text-destructive">Horse not found</div>;

  const r = horse.ranking || {};
  const breakdown = [
    { dim: "Performance", v: r.performance || 0 },
    { dim: "Consistency", v: r.consistency || 0 },
    { dim: "Rider", v: r.rider_feedback || 0 },
    { dim: "Level", v: r.level_strength || 0 },
    { dim: "Durability", v: r.durability || 0 },
    { dim: "Accuracy", v: r.accuracy || 0 },
  ];
  const trend = chukkas.slice().reverse().map((c, i) => {
    const vals = Object.keys(RATING_LABELS).map((k) => c[k] || 0);
    const score = (vals.reduce((a, b) => a + b, 0) / vals.length) / 5 * 100;
    return { i: i + 1, score: parseFloat(score.toFixed(1)), date: c.created_at };
  });

  return (
    <div className="space-y-6">
      {canDownloadReport && (
        <ShareHorseDialog horse={horse} open={shareOpen} onOpenChange={setShareOpen} />
      )}
      {/* Top nav strip — "All horses" plus prev/next buttons to scroll
          through the alphabetically-ordered horse list without going
          back to the index. Keyboard-friendly ← / → shortcuts wired
          below. Position/total gives the user a sense of where they
          are (e.g. "17 of 47"). */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Link to="/horses" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="horse-back-all">
          <ChevronLeft className="h-3 w-3" /> All horses
        </Link>
        <HorseNavStrip neighbors={neighbors} />
      </div>
      {/* Hero */}
      <Card className="rounded-xl overflow-hidden">
        <div className="p-6 sm:p-8 grid lg:grid-cols-12 gap-6" data-testid="horse-profile-hero">
          <HorsePhotoPanel horse={horse} onChange={load} videos={videos} />
          <div className="lg:col-span-6">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="ap-title-display" data-testid="horse-profile-name">{horse.name}</h1>
              <PoolPill pool={r.pool} stars={r.stars} />
              {horse.computed_class && (
                <span className="inline-flex items-center rounded-full border border-[hsl(var(--ap-brass)/0.4)] bg-[hsl(var(--ap-brass)/0.08)] px-2.5 py-0.5 text-[11px] font-medium text-[hsl(var(--ap-saddle))]" title="Auto-computed from age" data-testid="horse-class-pill">
                  {horse.computed_class}
                </span>
              )}
              {horse.injured && (
                <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--destructive)/0.35)] bg-[hsl(var(--destructive)/0.08)] px-2.5 py-0.5 text-[11px] font-medium text-[hsl(var(--destructive))]">
                  <span className="inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} /> Injured{horse.severity ? ` · ${horse.severity}` : ""}</span>
                </span>
              )}
              <BaseAssignControl
                value={horse.base_id}
                size="xs"
                testid="horse-base-assign"
                onAssign={async (bid) => {
                  try {
                    const res = await api.patch(`/horses/${horse.id}/base`, { base_id: bid });
                    setHorse((h) => ({ ...h, base_id: bid }));
                    toast.success(`Moved to ${res.data?.base_name || "base"}`);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Could not move horse");
                  }
                }}
              />
              {/* Sale-status pill — visible to admins/executives.
                  Explicit "Not for sale" callout so buyers don't waste
                  everyone's time enquiring about a horse that is off the
                  market. Rendered only when we already have a price to
                  show, otherwise the price panel below carries the
                  status via its own badge. */}
              {canSeePrice && (
                horse.status === "for_sale" ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--ap-brass)/0.45)] bg-[hsl(var(--ap-brass)/0.12)] text-[hsl(var(--ap-saddle))] px-2.5 py-0.5 text-[11px] font-medium" data-testid="horse-for-sale-pill">
                    <Tag className="h-3 w-3" strokeWidth={2} /> For sale
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 text-muted-foreground px-2.5 py-0.5 text-[11px] font-medium" data-testid="horse-not-for-sale-pill">
                    Not for sale
                  </span>
                )
              )}
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {horse.breed && <span><span className="text-foreground font-medium">Breed:</span> {horse.breed}</span>}
              {horse.sex && <span><span className="text-foreground font-medium">Sex:</span> {horse.sex}</span>}
              {horse.age != null && (
                <span className="inline-flex items-center gap-2">
                  <span className="text-foreground font-medium">Age:</span>
                  <HorseAgeBadge age={horse.age} />
                  {horse.age_months ? <span className="text-xs">({horse.age_months}mo)</span> : null}
                </span>
              )}
              {horse.color && <span><span className="text-foreground font-medium">Color:</span> {horse.color}</span>}
              {horse.height_hands && <span><span className="text-foreground font-medium">Height:</span> {horse.height_hands}h</span>}
              {horse.location && <span><span className="text-foreground font-medium">Location:</span> {horse.location}</span>}
              {horse.arrival_date && <span><span className="text-foreground font-medium">Arrived:</span> {horse.arrival_date}</span>}
              {horse.time_at_farm_days != null && <span><span className="text-foreground font-medium">Time at farm:</span> {horse.time_at_farm_days} days</span>}
              {horse.work_status && <span><span className="text-foreground font-medium">Work status:</span> {horse.work_status}</span>}
              <span><span className="text-foreground font-medium">Status:</span> {horse.status}</span>
            </div>
            {(horse.injury_comments || horse.vet_comments) && (
              <div className="mt-4 space-y-1 text-sm">
                {horse.injury_comments && <p className="text-[hsl(var(--destructive))]"><span className="font-medium">Injury:</span> {horse.injury_comments}</p>}
                {horse.vet_comments && <p className="text-muted-foreground"><span className="font-medium text-foreground">Vet:</span> {horse.vet_comments}</p>}
              </div>
            )}
            {horse.notes && <p className="mt-4 text-sm text-muted-foreground italic">&ldquo;{horse.notes}&rdquo;</p>}
          </div>
          <div className="flex flex-col items-end justify-between lg:col-span-3">
            <div className="text-right w-full">
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Ranking score</div>
              <div className="font-serif text-6xl tabular-nums leading-none mt-1" data-testid="horse-profile-score-text">{(r.score || 0).toFixed(1)}</div>
              <div className="text-xs text-muted-foreground mt-1">{r.chukkas_count || 0} horse rides tracked</div>
              {canDownloadReport && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={downloadReport}
                  disabled={reporting}
                  data-testid="horse-report-download-button"
                >
                  {reporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
                  Download ranking report
                </Button>
              )}
              {canDownloadReport && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 ml-0 sm:ml-2 text-[hsl(var(--ap-forest))] hover:text-[hsl(var(--ap-forest))]"
                  onClick={() => setShareOpen(true)}
                  data-testid="horse-report-share-button"
                >
                  <Share2 className="h-3.5 w-3.5 mr-1.5" />
                  Share link
                </Button>
              )}
              {/* Price panel — admins/executives/master only. Shows the
                  internal purchase price and, when a listed price has
                  been pushed, a live indicator + the listed number so
                  operators can tell at a glance whether re-pushing is
                  needed after an edit. */}
              {canSeePrice && (
                <div className="mt-4 rounded-lg border border-border/70 bg-background/60 p-3 text-right" data-testid="horse-price-panel">
                  <div className="flex items-center justify-end gap-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">
                    <DollarSign className="h-3 w-3" /> Internal price
                  </div>
                  <div className="font-serif text-2xl tabular-nums mt-0.5" data-testid="horse-price-value">
                    {Number.isFinite(horse.purchase_price)
                      ? formatCurrency(horse.purchase_price, "USD")
                      : <span className="text-muted-foreground text-base">Not set</span>}
                  </div>
                  {horse.purchased_from && (
                    <div className="text-[11px] text-muted-foreground mt-1 truncate" title={horse.purchased_from}>
                      Source: {horse.purchased_from}
                    </div>
                  )}
                  <PricePublishRow
                    horse={horse}
                    canPublishPrice={canPublishPrice}
                    onChange={load}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-4 justify-end">
              <EditHorseDialog horse={horse} onSaved={load} canSeePrice={canSeePrice} />
              <RatingDialog horseId={horse.id} horseName={horse.name} onSaved={load} />
              <HealthDialog horseId={horse.id} onSaved={load} />
              {canDeleteHorse && (
                <DeleteHorseButton
                  horse={horse}
                  onDeleted={() => {
                    toast.success(`${horse.name} deleted.`);
                    navigate("/horses");
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7 rounded-xl">
          <CardHeader>
            <CardTitle className="text-base">Performance trend</CardTitle>
            <p className="text-xs text-muted-foreground">Per-ride score (0-100)</p>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]" data-testid="horse-performance-trend-chart">
              {trend.length < 2 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Need at least 2 rides for a trend line.</div>
              ) : (
                <ResponsiveContainer>
                  <LineChart data={trend}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="i" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={[0, 100]} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Line type="monotone" dataKey="score" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-5 rounded-xl">
          <CardHeader>
            <CardTitle className="text-base">Ranking breakdown</CardTitle>
            <p className="text-xs text-muted-foreground">Formula v9 components</p>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]" data-testid="horse-ranking-breakdown-chart">
              <ResponsiveContainer>
                <RadarChart data={breakdown}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis dataKey="dim" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <PolarRadiusAxis stroke="hsl(var(--muted-foreground))" fontSize={10} domain={[0, 100]} />
                  <Radar dataKey="v" stroke="hsl(var(--chart-1))" fill="hsl(var(--chart-1))" fillOpacity={0.25} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              Fatigue penalty: <span className="text-foreground font-mono">{(r.fatigue_penalty || 0).toFixed(1)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI health & workload trends (hoof-abscess watch + in-work rides) */}
      {(isMaster || hasPerm?.("trends:read")) && <HorseTrendsSection horseId={id} />}
      {(isMaster || hasPerm?.("trends:read")) && <HorseRidersSeasons horseId={id} />}

      {/* Chukka log */}
      <Card className="rounded-xl">
        <CardHeader><CardTitle className="text-base">Ride history</CardTitle></CardHeader>
        <CardContent>
          {chukkas.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No rides yet. Submit a rating above or via WhatsApp <span className="font-mono">rate</span>.</p>
          ) : (
            <div className="overflow-auto" data-testid="horse-chukkas-list">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b">
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Rider</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Bit</th>
                    <th className="text-right px-3 py-2">Overall</th>
                    <th className="text-left px-3 py-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {chukkas.map((c) => {
                    // Legacy-import placeholder values ("Legacy import"
                    // stamped on rider_name / competition_type during
                    // the historical migration) are hidden from the UI
                    // — the operator team never wants to see the phrase.
                    // We fall back to an em-dash so the column still
                    // aligns visually.
                    const rider = stripLegacyLabel(c.rider_name);
                    const compType = stripLegacyLabel(c.competition_type);
                    return (
                    <tr key={c.id} className="border-b hover:bg-accent/60">
                      <td className="px-3 py-2 text-muted-foreground">{safeFormatDate(c.created_at, "MMM d HH:mm")}</td>
                      <td className="px-3 py-2">{rider || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2">{compType || "Practice"}</td>
                      <td className="px-3 py-2" data-testid={`horse-chukka-bit-${c.id}`}>{c.bit_used || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2 text-right font-mono">{c.overall}/5</td>
                      <td className="px-3 py-2 text-muted-foreground">{c.source}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Health */}
      <Card className="rounded-xl">
        <CardHeader><CardTitle className="text-base">Health log</CardTitle></CardHeader>
        <CardContent>
          {health.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No health events recorded.</p>
          ) : (
            <div className="space-y-2" data-testid="horse-health-list">
              {health.map((h) => (
                <div key={h.id} className="flex items-start gap-3 p-3 rounded-md border">
                  <Heart className="h-4 w-4 text-[hsl(var(--destructive))] mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between">
                      <span className="font-medium capitalize">{h.type}{h.severity ? `. ${h.severity}` : ""}</span>
                      <span className="text-xs text-muted-foreground">{safeDistanceToNow(h.created_at)} ago</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{h.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rating notes. free-text writeups from Step 19 of every /rate
          submission. Chronological, most-recent first, so an executive
          scanning the horse profile gets qualitative context (rider
          observations) alongside the quantitative ranking above. */}
      <Card className="rounded-xl" data-testid="horse-writeups-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[hsl(var(--ap-forest))]" />
            Rating notes
          </CardTitle>
          <p className="text-xs text-muted-foreground">Rider comments captured on step 19 of each session rating.</p>
        </CardHeader>
        <CardContent>
          {(() => {
            const notes = chukkas.filter((c) => (c.writeup || "").trim().length > 0);
            if (notes.length === 0) {
              return <p className="text-sm text-muted-foreground py-6 text-center">No rating notes yet. riders can add one on step 19 of the WhatsApp <span className="font-mono">rate</span> flow, or via the &ldquo;Add ride rating&rdquo; dialog above.</p>;
            }
            return (
              <div className="space-y-3" data-testid="horse-writeups-list">
                {notes.map((c) => {
                  // Same legacy-label suppression as the Ride history
                  // table above — never render "Legacy import" as a
                  // visible label. Blank rider falls back to the
                  // existing "Unknown rider" copy; blank comp-type
                  // just hides the pill.
                  const rider = stripLegacyLabel(c.rider_name);
                  const compType = stripLegacyLabel(c.competition_type);
                  return (
                  <div key={c.id} className="p-3 rounded-md border border-l-4 border-l-[hsl(var(--ap-forest))] bg-[hsl(var(--ap-paper))]/50" data-testid={`horse-writeup-${c.id}`}>
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{rider || "Unknown rider"}</span>
                        {compType && (
                          <span className="inline-flex items-center rounded-full bg-[hsl(var(--ap-forest)/0.1)] text-[hsl(var(--ap-forest))] px-2 py-0.5 text-[10px] font-medium capitalize">
                            {compType}
                          </span>
                        )}
                        {typeof c.overall === "number" && (
                          <span className="tabular-nums">overall {c.overall}/5</span>
                        )}
                        {c.bit_used && (
                          <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-medium" data-testid={`horse-writeup-bit-${c.id}`}>
                            {c.bit_used} bit
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {safeFormatDate(c.created_at, "MMM d, yyyy")} · {safeDistanceToNow(c.created_at)} ago
                      </span>
                    </div>
                    <p className="text-sm mt-1.5 whitespace-pre-wrap leading-relaxed">{c.writeup}</p>
                  </div>
                  );
                })}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Videos — bottom of the profile; also shown on the public share link */}
      <HorseVideosPanel horseId={id} canEdit={isMaster || hasPerm?.("horses:write")} videos={videos} onChanged={loadVideos} />
    </div>
  );
}

// Map a picked file to an allowed video MIME type. Falls back to the
// file extension because some browsers/OSes report an empty type for
// .mov / .m4v clips (which would otherwise fail the format check).
function guessVideoCtype(file) {
  const ct = (file.type || "").toLowerCase().split(";")[0].trim();
  if (ct) return ct;
  const name = (file.name || "").toLowerCase();
  if (name.endsWith(".mp4")) return "video/mp4";
  if (name.endsWith(".webm")) return "video/webm";
  if (name.endsWith(".mov")) return "video/quicktime";
  if (name.endsWith(".m4v")) return "video/x-m4v";
  return "";
}

// PUT a file straight to a presigned R2 URL with upload progress. This
// bypasses our backend/proxy for the heavy byte transfer, so large clips
// on slow connections no longer time out mid-upload.
function putToR2(url, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && onProgress) onProgress(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Storage upload failed (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Could not reach video storage (check R2 CORS settings)."));
    xhr.ontimeout = () => reject(new Error("Video storage upload timed out."));
    xhr.send(file);
  });
}

function HorseVideosPanel({ horseId, canEdit, videos: videosProp, onChanged }) {
  const videos = videosProp || null;
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [title, setTitle] = useState("");
  const fileRef = React.useRef(null);
  const base = process.env.REACT_APP_BACKEND_URL || "";

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error("Video is too large (max 200 MB)"); e.target.value = ""; return; }
    const ctype = guessVideoCtype(file);
    if (!ctype) { toast.error("Unsupported video format. Use MP4, WebM or MOV."); e.target.value = ""; return; }
    setUploading(true);
    setProgress(0);
    try {
      // 1) Ask our backend for a short-lived presigned upload URL.
      const pre = await api.post(`/horses/${horseId}/videos/presign`, {
        content_type: ctype, size: file.size, filename: file.name,
      });
      const { upload_url, video_id, key, ext, content_type } = pre.data;
      // 2) Upload the bytes directly to R2 (with progress).
      await putToR2(upload_url, file, content_type, setProgress);
      // 3) Tell the backend to record the clip's metadata.
      await api.post(`/horses/${horseId}/videos/confirm`, {
        video_id, key, ext, content_type, title: title.trim(), size: file.size,
      });
      toast.success("Video uploaded");
      setTitle("");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (vid) => {
    try { await api.delete(`/horses/${horseId}/videos/${vid}`); toast.success("Video removed"); onChanged?.(); }
    catch (_e) { toast.error("Could not remove"); }
  };

  return (
    <Card className="rounded-xl" data-testid="horse-videos-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Video className="h-4 w-4 text-[hsl(var(--ap-forest))]" /> Videos
          {videos != null && <span className="text-xs text-muted-foreground font-normal">· {videos.length}</span>}
        </CardTitle>
        {canEdit && (
          <p className="text-xs text-muted-foreground">MP4, WebM or MOV · up to 200&nbsp;MB each. Shown on the public share link too.</p>
        )}
      </CardHeader>
      <CardContent>
        {canEdit && (
          <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border p-3">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs">Title (optional)</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Stick & ball session" data-testid="video-title-input" />
            </div>
            <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/x-m4v" hidden onChange={onPick} data-testid="video-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="video-upload-btn"
              className="bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90">
              {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              {uploading ? (progress > 0 ? `Uploading… ${progress}%` : "Uploading…") : "Add video"}
            </Button>
          </div>
        )}

        {videos == null ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
        ) : videos.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="horse-videos-empty">
            No videos yet.{canEdit ? " Add one above." : ""}
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2" data-testid="horse-videos-grid">
            {videos.map((v) => (
              <div key={v.video_id} className="rounded-lg border overflow-hidden" data-testid={`horse-video-${v.video_id}`}>
                <video src={`${base}${v.url}`} controls preload="metadata" playsInline
                  className="w-full aspect-video bg-black" data-testid={`horse-video-player-${v.video_id}`} />
                <div className="flex items-center justify-between gap-2 p-2.5">
                  <span className="text-sm truncate" title={v.title || ""}>{v.title || "Untitled clip"}</span>
                  {canEdit && (
                    <ConfirmDialog title="Remove this video?" description="This permanently deletes the clip." confirmLabel="Remove" onConfirm={() => remove(v.video_id)}>
                      <button className="shrink-0 text-muted-foreground hover:text-destructive p-1" aria-label="Delete video" data-testid={`horse-video-delete-${v.video_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </ConfirmDialog>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RatingDialog({ horseId, horseName, onSaved }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    horse_id: horseId, rider_name: "", rider_handicap: 0, competition_type: "Practice",
    bit_used: "",
    ...Object.keys(RATING_LABELS).reduce((a, k) => ({ ...a, [k]: 3 }), {}),
  });

  const fields = Object.keys(RATING_LABELS);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/horses/${horseId}/chukkas`, {
        ...form,
        rider_handicap: parseFloat(form.rider_handicap) || 0,
        bit_used: (form.bit_used || "").trim() || null,
      });
      toast.success("Rating saved. score recomputed");
      setOpen(false); setStep(0);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="horse-add-rating-button"><Sparkles className="h-4 w-4 mr-2" /> Add ride rating</Button></DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Rate {horseName}. post-ride</DialogTitle></DialogHeader>
        {step === 0 ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Rider name</Label>
              <Input value={form.rider_name} onChange={(e) => setForm({ ...form, rider_name: e.target.value })} data-testid="rating-rider-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Rider handicap</Label>
              <Input type="number" value={form.rider_handicap} onChange={(e) => setForm({ ...form, rider_handicap: e.target.value })} data-testid="rating-rider-handicap" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Competition</Label>
              <Select value={form.competition_type} onValueChange={(v) => setForm({ ...form, competition_type: v })}>
                <SelectTrigger data-testid="rating-competition-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Practice", "Friendly", "Tournament", "Championship"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bit used (optional)</Label>
              <Input
                value={form.bit_used}
                onChange={(e) => setForm({ ...form, bit_used: e.target.value })}
                placeholder="e.g. 3 ring dancer, Big ring control plate"
                data-testid="rating-bit-used"
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-auto pr-2">
            {fields.map((f) => (
              <div key={f}>
                <Label className="text-xs text-muted-foreground">{RATING_LABELS[f]} ({form[f]}/5)</Label>
                <input type="range" min="1" max="5" step="1" value={form[f]} onChange={(e) => setForm({ ...form, [f]: parseInt(e.target.value) })} className="w-full accent-[hsl(var(--ap-forest))]" data-testid={`rating-${f}`} />
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          {step === 0 ? (
            <Button onClick={() => setStep(1)} disabled={!form.rider_name} data-testid="rating-next-button">Next: ratings</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStep(0)}>Back</Button>
              <Button onClick={submit} disabled={saving} data-testid="rating-submit-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & recompute score"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HealthDialog({ horseId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: "injury", description: "", severity: "mild" });
  const submit = async () => {
    if (!form.description) { toast.error("Description required"); return; }
    setSaving(true);
    try {
      await api.post(`/horses/${horseId}/health`, { ...form, horse_id: horseId });
      toast.success("Health log added");
      setOpen(false); setForm({ type: "injury", description: "", severity: "mild" });
      onSaved();
    } catch (e) {
      toast.error("Failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="secondary" data-testid="horse-add-health-button"><Heart className="h-4 w-4 mr-2" /> Log health</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Log a health event</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="health-type"><SelectValue /></SelectTrigger>
            <SelectContent>{["injury","lame","treatment","vet_visit","note"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
            <SelectTrigger data-testid="health-severity"><SelectValue /></SelectTrigger>
            <SelectContent>{["mild","moderate","severe"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea placeholder="Description…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="health-description" />
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving} data-testid="health-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditHorseDialog({ horse, onSaved, canSeePrice }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  // Confirmation dialog shown when the admin has changed the age.
  // Age is business-critical in the polo world — the rising
  // convention (see /app/frontend/src/lib/polo-age.js) means any
  // change here has ripple effects on how the horse is described
  // for the rest of the calendar year. We block the save with a
  // deliberate confirm rather than silently accepting the change.
  const [ageConfirmOpen, setAgeConfirmOpen] = useState(false);

  const openIt = () => {
    setForm({
      name: horse.name || "",
      breed: horse.breed || "",
      sex: horse.sex || "",
      age: horse.age ?? "",
      color: horse.color || "",
      height_hands: horse.height_hands ?? "",
      status: horse.status || "playing",
      notes: horse.notes || "",
      location: horse.location || "",
      arrival_date: horse.arrival_date || "",
      work_status: horse.work_status || "",
      injured: !!horse.injured,
      injury_comments: horse.injury_comments || "",
      severity: horse.severity || "",
      vet_comments: horse.vet_comments || "",
      // Financials — only rendered / submitted when the caller has the
      // `canSeePrice` gate (admin/executive/master). Blank string is
      // treated as "clear" on save.
      purchase_price: horse.purchase_price ?? "",
      purchased_from: horse.purchased_from || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    // Polo age caution — if the age differs from the value we opened
    // the dialog with, route through a confirmation step before we
    // actually PUT. `parseFloat` normalises "" vs null vs number so
    // an unchanged age doesn't accidentally re-trigger the modal.
    const originalAge = horse.age == null || horse.age === "" ? null : parseFloat(horse.age);
    const newAge = form.age === "" || form.age == null ? null : parseFloat(form.age);
    const ageChanged = originalAge !== newAge;
    if (ageChanged && !ageConfirmOpen) {
      // Open the polo-age confirmation instead of saving. The confirm
      // handler calls save() again with `ageConfirmOpen=true` so we
      // skip this branch on the second pass.
      setAgeConfirmOpen(true);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: form.age === "" ? null : parseFloat(form.age),
        height_hands: form.height_hands === "" ? null : parseFloat(form.height_hands),
        arrival_date: form.arrival_date || null,
        location: form.location || null,
        work_status: form.work_status || null,
        severity: form.severity || null,
        injury_comments: form.injury_comments || null,
        vet_comments: form.vet_comments || null,
      };
      if (canSeePrice) {
        // Only include financial fields if the editor was authorised to see
        // them — this prevents an accidental blank-out from a lower-priv
        // user's session state ever nulling a price on save.
        payload.purchase_price =
          form.purchase_price === "" || form.purchase_price == null
            ? null
            : parseFloat(form.purchase_price);
        payload.purchased_from = form.purchased_from ? form.purchased_from.trim() : null;
        if (payload.purchase_price !== null && !Number.isFinite(payload.purchase_price)) {
          toast.error("Purchase price must be a number");
          setSaving(false);
          return;
        }
      } else {
        delete payload.purchase_price;
        delete payload.purchased_from;
      }
      await api.put(`/horses/${horse.id}`, payload);
      toast.success("Horse updated. class recomputed");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="secondary" onClick={openIt} data-testid="horse-edit-button"><Pencil className="h-4 w-4 mr-2" /> Edit details</Button>
      {form && (
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Edit {horse.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Cell label="Name *"><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></Cell>
            <Cell label="Age (years)">
              <Input type="number" step="0.25" value={form.age} onChange={(e) => setForm({...form, age: e.target.value})} data-testid="edit-horse-age" />
              {/* Inline polo-convention caution. Shows unconditionally on
                  edit so the admin is reminded that age isn't just a
                  number — from 1 Jan 2027 onwards this field drives
                  the "Rising N" display + polo commercial category. */}
              <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                ⚠️ Polo convention: from 1 Jan 2027, every horse is displayed as{" "}
                <span className="font-medium text-foreground">
                  Rising {form.age && !Number.isNaN(parseFloat(form.age)) ? Math.floor(parseFloat(form.age)) + 1 : "N+1"}
                </span>{" "}
                for the entire calendar year. Only bump the stored age when the horse has actually turned.
              </p>
            </Cell>
            <Cell label="Sex"><Select value={form.sex || "__"} onValueChange={(v) => setForm({...form, sex: v === "__" ? "" : v})}><SelectTrigger><SelectValue placeholder="" /></SelectTrigger><SelectContent>{["__","gelding","mare","stallion","filly","colt"].map((o) => <SelectItem key={o} value={o}>{o === "__" ? "" : o}</SelectItem>)}</SelectContent></Select></Cell>
            <Cell label="Breed"><Input value={form.breed} onChange={(e) => setForm({...form, breed: e.target.value})} /></Cell>
            <Cell label="Colour"><Input value={form.color} onChange={(e) => setForm({...form, color: e.target.value})} /></Cell>
            <Cell label="Height (hands)"><Input type="number" step="0.25" value={form.height_hands} onChange={(e) => setForm({...form, height_hands: e.target.value})} /></Cell>
            <Cell label="Location"><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} placeholder="Grazing, Stables, or other…" data-testid="edit-horse-location" /></Cell>
            <Cell label="Arrival date"><Input type="date" value={form.arrival_date} onChange={(e) => setForm({...form, arrival_date: e.target.value})} data-testid="edit-horse-arrival-date" /></Cell>
            <Cell label="Work status">
              <Select value={form.work_status || "__"} onValueChange={(v) => setForm({...form, work_status: v === "__" ? "" : v})}>
                <SelectTrigger data-testid="edit-horse-work-status"><SelectValue placeholder="" /></SelectTrigger>
                <SelectContent>{["__","Playing","In work","Resting","Going back to Stydrom","Fattening"].map((o) => <SelectItem key={o} value={o}>{o === "__" ? "" : o}</SelectItem>)}</SelectContent>
              </Select>
            </Cell>
            <Cell label="Status (system)">
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["playing","young","resting","brood","injured","for_sale"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Cell>
            <div className="col-span-2 border-t pt-3">
              <div className="flex items-center gap-2 mb-2">
                <input type="checkbox" id="edit-injured" checked={form.injured} onChange={(e) => setForm({...form, injured: e.target.checked})} className="h-4 w-4" data-testid="edit-horse-injured" />
                <label htmlFor="edit-injured" className="text-sm font-medium">Currently injured</label>
              </div>
              {form.injured && (
                <div className="grid grid-cols-2 gap-3">
                  <Cell label="Injury comments" colSpan={2}><Input value={form.injury_comments} onChange={(e) => setForm({...form, injury_comments: e.target.value})} /></Cell>
                  <Cell label="Severity">
                    <Select value={form.severity || "__"} onValueChange={(v) => setForm({...form, severity: v === "__" ? "" : v})}>
                      <SelectTrigger><SelectValue placeholder="" /></SelectTrigger>
                      <SelectContent>{["__","None","Mild","Chronic"].map((o) => <SelectItem key={o} value={o}>{o === "__" ? "" : o}</SelectItem>)}</SelectContent>
                    </Select>
                  </Cell>
                </div>
              )}
              <div className="mt-3">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Vet comments</Label>
                <Input value={form.vet_comments} onChange={(e) => setForm({...form, vet_comments: e.target.value})} />
              </div>
            </div>
            <Cell label="Notes" colSpan={2}><Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></Cell>
            {/* Financials — visible only to roles that can see the horse's
                asking price. USD amounts (horses priced in dollars while
                operational expenses stay in ZAR). */}
            {canSeePrice && (
              <div className="col-span-2 border-t pt-3 grid grid-cols-2 gap-3" data-testid="edit-horse-financials">
                <Cell label="Purchase price (USD)">
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    value={form.purchase_price}
                    onChange={(e) => setForm({...form, purchase_price: e.target.value})}
                    placeholder="e.g. 8000"
                    data-testid="edit-horse-purchase-price"
                  />
                </Cell>
                <Cell label="Purchased from">
                  <Input
                    value={form.purchased_from}
                    onChange={(e) => setForm({...form, purchased_from: e.target.value})}
                    placeholder="e.g. Stydrom Stud"
                    data-testid="edit-horse-purchased-from"
                  />
                </Cell>
              </div>
            )}
            {form.age !== "" && (
              <div className="col-span-2 rounded-md border bg-[hsl(var(--ap-paper))] p-3 text-xs">
                <span className="text-muted-foreground">Class will be recomputed to:</span>{" "}
                <span className="font-medium">{previewClassDetail(form.age, form.arrival_date)}</span>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={save} disabled={saving} data-testid="edit-horse-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button></DialogFooter>
        </DialogContent>
      )}
      {/* Polo-age caution — deliberately verbose because this business
          rule is easy to fluff (e.g. an admin bumping age from 4 to 5
          on 31 Dec 23:59 accidentally puts the horse on "Rising 6" one
          minute later). Master admins can still push through, but
          only after acknowledging the effect. */}
      {form && (
        <AlertDialog open={ageConfirmOpen} onOpenChange={setAgeConfirmOpen}>
          <AlertDialogContent data-testid="age-change-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Change {horse.name}'s age?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  <p>
                    You're changing the stored age from{" "}
                    <span className="font-medium text-foreground">
                      {horse.age == null || horse.age === "" ? "—" : `${horse.age}`}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium text-foreground">
                      {form.age === "" || form.age == null ? "—" : `${form.age}`}
                    </span>.
                  </p>
                  {form.age !== "" && form.age != null && !Number.isNaN(parseFloat(form.age)) && parseFloat(form.age) >= 2 && (
                    <p className="rounded-md border bg-[hsl(var(--ap-paper))] p-3 text-[13px]">
                      <span className="font-medium">Polo convention reminder:</span> from 1 January 2027 onwards, this
                      horse will display as{" "}
                      <span className="font-medium text-[hsl(var(--ap-brass))]">
                        Rising {Math.floor(parseFloat(form.age)) + 1}
                      </span>{" "}
                      for the entire calendar year. If today is 31 December, this label kicks in the moment the clock
                      rolls past midnight.
                    </p>
                  )}
                  <p className="text-muted-foreground">
                    Only bump the stored age when the horse has physically turned. Are you sure this is the new correct age?
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="age-change-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => { setAgeConfirmOpen(false); save(); }}
                data-testid="age-change-confirm"
              >
                Yes, change age
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Dialog>
  );
}

function Cell({ label, colSpan = 1, children }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function previewClassDetail(age, arrivalDate) {
  // Mirrors backend/horses.py::compute_horse_class — see that file for the
  // canonical Excel formula. Used only for the EditHorseDialog preview.
  const ageNum = parseFloat(age);
  if (!Number.isFinite(ageNum)) return "";
  // Hard age gate: horses ≤ 3 years are not yet rideable — always Blocked.
  if (ageNum <= 3) return "Blocked (Age ≤ 3) - Not yet rideable";
  if (!arrivalDate || arrivalDate === "∞") return "Playing";
  const arrival = toDate(arrivalDate);
  if (!arrival) return "Playing";
  const now = new Date();
  let m = (now.getFullYear() - arrival.getFullYear()) * 12 + (now.getMonth() - arrival.getMonth());
  if (now.getDate() < arrival.getDate()) m -= 1;
  if (m < 0) m = 0;
  if (m < 3) return "Class 1 - Trotting Chukkas";
  if (m < 6) return "Class 2 - Canter & Keepaway";
  if (m < 9) return "Class 3 - August Tournaments and Formative chukkas";
  if (m < 12) return "Class 4 - 2-4 Goal Tournaments";
  return "Playing";
}

function HorsePhotoPanel({ horse, onChange, videos }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const base = process.env.REACT_APP_BACKEND_URL || "";
  // Bust the browser cache when a new upload completes by appending a
  // timestamp param — otherwise the <img> would keep showing the stale image.
  const [cacheBuster, setCacheBuster] = useState(0);
  // Photo history — newest first. Populated via /horses/{id}/photos so
  // the detail-page gallery can show every historical picture, while
  // thumbnails on WhatsApp / horses list / dashboard still resolve
  // through the "current photo" URL as before.
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const hasPhoto = Boolean(horse?.photo);
  const cardSrc = hasPhoto
    ? `${horse.photo_card_url || `/api/horses/${horse.id}/photo/card`}${cacheBuster ? `?v=${cacheBuster}` : ""}`
    : null;

  const uploadedDate = safeFormatDate(horse?.photo?.uploaded_at, "MMM d, yyyy", null);

  // Load the photo history whenever the horse identity changes or a
  // fresh upload just landed. We re-key off cacheBuster so a new upload
  // triggers a refresh even though horse.id stays constant.
  useEffect(() => {
    if (!horse?.id) return;
    let cancelled = false;
    (async () => {
      setHistoryLoading(true);
      try {
        const res = await api.get(`/horses/${horse.id}/photos`);
        if (!cancelled) setHistory(res.data?.photos || []);
      } catch (_e) {
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [horse?.id, cacheBuster]);

  // Build the lightbox items list from the FULL history so the arrows
  // walk through past photos in chronological order. Each item mimics
  // the horse-shaped object PhotoLightbox expects (id, name, photo_url,
  // photo_card_url, uploaded_at) — but with per-photo URLs so we render
  // the right historical image at each index.
  //
  // The first entry (index 0) is the current/latest photo, addressed
  // via the horse-level "/photo/full" URL so the browser can share the
  // cached image with the panel's card view above.
  const photoItems = useMemo(() => {
    if (!horse || !history.length) return hasPhoto && horse ? [horse] : [];
    return history.map((p, i) => ({
      id: `${horse.id}:${p.photo_id}`,
      horse_id: horse.id,
      name: horse.name,
      // Latest photo uses the stable "current" URL so it stays cache-shared
      // with the main card. Older entries use the per-photo URL.
      photo_url: i === 0
        ? `${horse.photo_url || `/api/horses/${horse.id}/photo/full`}${cacheBuster ? `?v=${cacheBuster}` : ""}`
        : `/api/horses/${horse.id}/photos/${p.photo_id}/full`,
      photo_card_url: i === 0
        ? `${horse.photo_card_url || `/api/horses/${horse.id}/photo/card`}${cacheBuster ? `?v=${cacheBuster}` : ""}`
        : `/api/horses/${horse.id}/photos/${p.photo_id}/card`,
      uploaded_at: p.uploaded_at,
      _photo_id: p.photo_id,
    }));
  }, [horse, history, hasPhoto, cacheBuster]);

  // Video clips (managed in the panel further down the page) are appended
  // after the photos so the full-screen viewer flows photos → videos and
  // the arrow keys walk through every piece of media in one place.
  const videoItems = useMemo(() => (videos || []).map((v) => ({
    id: `${horse?.id}:vid:${v.video_id}`,
    kind: "video",
    horse_id: horse?.id,
    name: horse?.name,
    title: v.title || "Video clip",
    video_url: `${base}${v.url}`,
    uploaded_at: v.uploaded_at,
  })), [videos, horse?.id, horse?.name, base]);

  const lightboxItems = useMemo(() => [...photoItems, ...videoItems], [photoItems, videoItems]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset so the same file can be re-selected
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image file (jpg / png / webp / heic).");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      await api.post(`/horses/${horse.id}/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCacheBuster(Date.now());
      toast.success("Photo saved");
      onChange?.();
    } catch (err) {
      toast.error(`Upload failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async () => {
    // Confirmation now handled inline via ConfirmDialog wrapping the
    // "Remove all" trigger — see the button JSX below.
    setDeleting(true);
    try {
      await api.delete(`/horses/${horse.id}/photo`);
      toast.success("All photos removed");
      onChange?.();
    } catch (err) {
      toast.error(`Delete failed: ${err?.response?.data?.detail || err.message}`);
      throw err; // let ConfirmDialog know so it doesn't auto-close on error
    } finally {
      setDeleting(false);
    }
  };

  // Delete a single historical photo (not the whole album). If the user
  // deletes the current/latest, the backend automatically promotes the
  // next-most-recent entry to be the new current — WhatsApp thumbnails
  // and the horses list re-resolve on their next fetch.
  const onDeleteEntry = async (photoId) => {
    if (!photoId) return;
    setDeletingId(photoId);
    try {
      await api.delete(`/horses/${horse.id}/photos/${photoId}`);
      setCacheBuster(Date.now()); // triggers history reload + card refresh
      toast.success("Photo removed from history");
      onChange?.();
    } catch (err) {
      toast.error(`Delete failed: ${err?.response?.data?.detail || err.message}`);
      throw err;
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="lg:col-span-3" data-testid="horse-photo-panel">
      <button
        type="button"
        onClick={() => { if (hasPhoto) { setLightboxIndex(0); setLightboxOpen(true); } }}
        disabled={!hasPhoto}
        className={`relative aspect-[4/5] w-full max-w-[320px] mx-auto overflow-hidden rounded-xl border border-border bg-muted/30 block ${
          hasPhoto ? "cursor-zoom-in transition-transform hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" : "cursor-default"
        }`}
        aria-label={hasPhoto ? `Enlarge photo of ${horse.name}` : "No photo"}
        data-testid="horse-photo-open-lightbox"
      >
        {hasPhoto ? (
          <img
            src={cardSrc}
            alt={horse.name}
            className="h-full w-full object-cover object-center"
            data-testid="horse-photo-img"
          />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center text-muted-foreground gap-2 p-4 text-center">
            <Camera className="h-8 w-8 opacity-60" />
            <div className="text-xs">No photo yet</div>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
      </button>
      {uploadedDate && (
        <div className="mt-2 text-center text-[11px] text-muted-foreground" data-testid="horse-photo-uploaded-at">
          <span className="inline-flex items-center justify-center gap-1"><Camera className="h-3 w-3" strokeWidth={1.5} /> {uploadedDate}</span>
        </div>
      )}
      <div className="mt-3 flex justify-center gap-2 max-w-[320px] mx-auto">
        <label className="cursor-pointer">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPick}
            disabled={uploading}
            data-testid="horse-photo-upload-input"
          />
          <span
            className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
            data-testid="horse-photo-upload-btn"
          >
            <Camera className="h-3.5 w-3.5" />
            {hasPhoto ? "Add new" : "Upload"}
          </span>
        </label>
        {hasPhoto && (
          <ConfirmDialog
            title={`Remove ALL photos for ${horse.name}?`}
            description={
              <>
                <p>Every historical photo of <span className="font-medium text-foreground">{horse.name}</span> will be permanently deleted from the album, including any past-photo history entries.</p>
                <p className="mt-2 text-[hsl(var(--destructive))] font-medium">This cannot be undone.</p>
              </>
            }
            confirmLabel="Yes, delete all photos"
            onConfirm={onDelete}
            testId="horse-photo-delete"
          >
            <button
              type="button"
              disabled={deleting}
              className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              data-testid="horse-photo-delete-btn"
            >
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Remove all
            </button>
          </ConfirmDialog>
        )}
      </div>

      {/* ── Photo history strip ─────────────────────────────────────
          Renders every historical picture as a clickable thumbnail.
          Only shown if there's MORE than one entry — otherwise the main
          card above already covers it. Latest is on the left (index 0)
          and matches the main card; each subsequent thumb walks back in
          time. Clicking a thumb opens the lightbox at that photo. */}
      {history.length > 1 && (
        <div className="mt-4 max-w-[520px] mx-auto" data-testid="horse-photo-history">
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              Past photos
            </div>
            <div className="text-[11px] text-muted-foreground tabular-nums" data-testid="horse-photo-history-count">
              {history.length} total
            </div>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2" data-testid="horse-photo-history-grid">
            {history.map((p, i) => {
              const isCurrent = i === 0;
              const thumbSrc = isCurrent
                ? `${horse.photo_thumb_url || `/api/horses/${horse.id}/photo/thumb`}${cacheBuster ? `?v=${cacheBuster}` : ""}`
                : `/api/horses/${horse.id}/photos/${p.photo_id}/thumb`;
              const uploadedLabel = safeFormatDate(p.uploaded_at, "MMM d, yyyy", "unknown date");
              return (
                <div key={p.photo_id} className="relative group" data-testid={`horse-photo-history-item-${p.photo_id}`}>
                  <button
                    type="button"
                    onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                    className={`relative aspect-square w-full overflow-hidden rounded-md border block transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
                      isCurrent
                        ? "border-primary/70 ring-1 ring-primary/40"
                        : "border-border hover:border-primary/60"
                    }`}
                    aria-label={`View photo from ${uploadedLabel}${isCurrent ? " (current)" : ""}`}
                    data-testid={`horse-photo-history-thumb-${p.photo_id}`}
                    title={uploadedLabel + (isCurrent ? " · current" : "")}
                  >
                    <img
                      src={thumbSrc}
                      alt={`${horse.name}. ${uploadedLabel}`}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                    {isCurrent && (
                      <span className="absolute bottom-0 inset-x-0 bg-primary/85 text-primary-foreground text-[9px] uppercase tracking-wider py-0.5 text-center font-medium">
                        Current
                      </span>
                    )}
                  </button>
                  {/* Per-thumbnail delete affordance. only visible on hover so
                      the grid stays tidy. Deleting the current photo promotes
                      the next-newest to become current server-side. */}
                  <ConfirmDialog
                    title={`Delete this photo?`}
                    description={
                      <>
                        <p>The photo from <span className="font-medium text-foreground">{uploadedLabel}</span> will be removed from {horse.name}&rsquo;s history. Other photos are kept.</p>
                        {isCurrent && (
                          <p className="mt-2 text-xs">This is the <span className="font-medium text-foreground">current</span> photo — deleting it will promote the next-newest photo to be the new current, which is what WhatsApp and the horses list will show.</p>
                        )}
                      </>
                    }
                    confirmLabel="Yes, delete photo"
                    onConfirm={() => onDeleteEntry(p.photo_id)}
                    testId={`horse-photo-history-delete-${p.photo_id}`}
                  >
                    <button
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      disabled={deletingId === p.photo_id}
                      className="absolute top-1 right-1 rounded-full bg-background/85 text-muted-foreground hover:text-destructive hover:bg-background p-1 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                      aria-label={`Delete this photo (${uploadedLabel})`}
                      data-testid={`horse-photo-history-delete-${p.photo_id}`}
                      title="Delete this photo"
                    >
                      {deletingId === p.photo_id
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <Trash2 className="h-3 w-3" />}
                    </button>
                  </ConfirmDialog>
                </div>
              );
            })}
          </div>
          <div className="mt-2 text-center text-[10px] text-muted-foreground">
            Tap a thumbnail to view. The current photo is what WhatsApp and the horses list show.
          </div>
        </div>
      )}

      {/* ── Video strip ──────────────────────────────────────────────
          Clips managed further down the page surface here as tappable
          tiles that open the SAME full-screen viewer as the photos, so
          you can flick between pictures and videos with the arrow keys. */}
      {videos && videos.length > 0 && (
        <div className="mt-4 max-w-[520px] mx-auto" data-testid="horse-video-strip">
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium inline-flex items-center gap-1">
              <Video className="h-3 w-3" strokeWidth={1.75} /> Videos
            </div>
            <div className="text-[11px] text-muted-foreground tabular-nums" data-testid="horse-video-strip-count">
              {videos.length} total
            </div>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2" data-testid="horse-video-strip-grid">
            {videos.map((v, j) => (
              <button
                key={v.video_id}
                type="button"
                onClick={() => { setLightboxIndex(photoItems.length + j); setLightboxOpen(true); }}
                className="relative aspect-square w-full overflow-hidden rounded-md border border-border bg-black block transition-all hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                aria-label={`Play ${v.title || "video clip"} in full screen`}
                data-testid={`horse-video-thumb-${v.video_id}`}
                title={v.title || "Video clip"}
              >
                <video
                  src={`${base}${v.url}`}
                  muted
                  preload="metadata"
                  playsInline
                  className="h-full w-full object-cover opacity-75"
                />
                <span className="absolute inset-0 flex items-center justify-center">
                  <PlayCircle className="h-7 w-7 text-white drop-shadow-lg" strokeWidth={1.75} />
                </span>
              </button>
            ))}
          </div>
          <div className="mt-2 text-center text-[10px] text-muted-foreground">
            Tap to play in the full-screen viewer, alongside the photos.
          </div>
        </div>
      )}
      {historyLoading && history.length === 0 && hasPhoto && (
        <div className="mt-4 text-center text-[11px] text-muted-foreground" data-testid="horse-photo-history-loading">
          Loading photo history…
        </div>
      )}

      {lightboxOpen && lightboxItems.length > 0 && (
        <PhotoLightbox
          items={lightboxItems}
          index={Math.min(lightboxIndex, lightboxItems.length - 1)}
          onIndex={(i) => setLightboxIndex(i)}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}


/**
 * Destructive-action button for removing a horse from the herd.
 *
 * Wraps a bin-icon button in an <AlertDialog> so accidental clicks
 * cannot destroy months of ride history + photos in a single tap. The
 * dialog forces a two-step confirmation and clearly states what is
 * about to be lost (chukka ratings, health log, photos). The backend
 * endpoint is gated by the `horses:delete` permission — this UI is a
 * belt-and-braces defence layer.
 */
function DeleteHorseButton({ horse, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/horses/${horse.id}`);
      setOpen(false);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete this horse");
    } finally {
      setDeleting(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          className="text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.35)] hover:bg-[hsl(var(--destructive)/0.08)] hover:text-[hsl(var(--destructive))]"
          data-testid="horse-delete-button"
          aria-label={`Delete ${horse.name}`}
          title={`Delete ${horse.name}`}
        >
          <Trash2 className="h-4 w-4 mr-2" /> Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="horse-delete-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[hsl(var(--destructive))]" />
            Delete {horse.name}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently remove <span className="font-medium text-foreground">{horse.name}</span>{" "}
            from the herd, along with:
            <ul className="mt-2 ml-5 list-disc space-y-0.5 text-xs">
              <li>Every ride rating and rider write-up</li>
              <li>The full health / injury log</li>
              <li>All photos in the horse&rsquo;s photo history</li>
              <li>Ranking history and any master-admin overrides</li>
            </ul>
            <p className="mt-3 font-medium text-[hsl(var(--destructive))]">This action cannot be undone.</p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting} data-testid="horse-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); doDelete(); }}
            disabled={deleting}
            className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90 focus:ring-[hsl(var(--destructive))]"
            data-testid="horse-delete-confirm"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Yes, delete permanently
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Publish-price micro-widget rendered under the internal price panel
 * on the horse detail hero.
 *
 * Shows one of three states:
 *   1. No internal price yet → hidden entirely. There's nothing to
 *      push, and the parent panel already prompts to set one.
 *   2. Internal price set, not yet pushed OR internal changed since
 *      last push → shows a "Push to rankings" button and, if there is
 *      a stale listed price, surfaces it in a subtle line so the
 *      operator knows what the world currently sees.
 *   3. Internal price set, listed_price matches → shows a green "Live
 *      on rankings" pill + an unpublish action.
 *
 * All state transitions dispatch `horses:prices-published` so any
 * RankingsPage instance in the same browser refreshes instantly.
 */
function PricePublishRow({ horse, canPublishPrice, onChange }) {
  const [busy, setBusy] = useState(false);
  const [togglingForSale, setTogglingForSale] = useState(false);
  const hasPurchase = Number.isFinite(horse?.purchase_price);
  const hasListed = Number.isFinite(horse?.listed_price);
  const inSync = hasPurchase && hasListed && Math.abs(horse.listed_price - horse.purchase_price) < 0.5;
  const isForSale = horse?.status === "for_sale";
  if (!canPublishPrice) return null;
  // Toggle the horse's `status` between `for_sale` and `playing`. Kept
  // deliberately close to the push button in the UI because the two
  // decisions are commercially linked — a price without a for-sale
  // flag doesn't reach interested buyers, and marking for sale
  // without a live price sends people to enquiry forms unnecessarily.
  const toggleForSale = async (next) => {
    setTogglingForSale(true);
    try {
      await api.put(`/horses/${horse.id}`, {
        ...horse,
        status: next ? "for_sale" : "playing",
      });
      toast.success(next ? `${horse.name} marked as For sale` : `${horse.name} taken off market`);
      window.dispatchEvent(new CustomEvent("horses:prices-published"));
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    } finally { setTogglingForSale(false); }
  };
  const push = async () => {
    setBusy(true);
    try {
      await api.post("/horses/publish-prices", { horse_ids: [horse.id] });
      toast.success("Price pushed to rankings");
      window.dispatchEvent(new CustomEvent("horses:prices-published"));
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed");
      throw e;
    } finally { setBusy(false); }
  };
  const unpublish = async () => {
    setBusy(true);
    try {
      await api.post("/horses/unpublish-prices", { horse_ids: [horse.id] });
      toast.success("Price unpublished");
      window.dispatchEvent(new CustomEvent("horses:prices-published"));
      onChange?.();
    } catch (e) {
      toast.error("Unpublish failed");
      throw e;
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 border-t border-border/60 pt-2 space-y-2" data-testid="horse-price-publish-row">
      {/* For-sale switch — always visible so operators can flip
          availability without digging into the full Edit dialog. */}
      <div className="flex items-center justify-between gap-3">
        <div className="text-left flex-1">
          <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">Sale status</div>
          <div className="text-xs text-foreground mt-0.5">
            {isForSale ? "On the market" : "Not currently for sale"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {togglingForSale && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          <Switch
            checked={isForSale}
            onCheckedChange={(v) => toggleForSale(!!v)}
            disabled={togglingForSale}
            aria-label="Toggle for sale status"
            data-testid="horse-for-sale-switch"
          />
        </div>
      </div>
      {/* Price push controls — the operator's price + for-sale flag
          together define the "public listing". */}
      {hasPurchase && (
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {inSync ? (
            <>
              <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--ap-forest)/0.4)] bg-[hsl(var(--ap-forest)/0.10)] text-[hsl(var(--ap-forest))] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider" data-testid="horse-price-live-pill">
                <CheckCircle2 className="h-3 w-3" strokeWidth={2.4} /> Live on rankings
              </span>
              <ConfirmDialog
                title={`Unpublish ${horse.name}'s price?`}
                description={
                  <p>{horse.name}'s price will be removed from the rankings page. Staff without price permissions will see it disappear on their next auto-refresh (within 60 seconds).</p>
                }
                confirmLabel="Yes, unpublish"
                destructive={false}
                onConfirm={unpublish}
                testId={`horse-price-unpublish-${horse.id}`}
              >
                <Button variant="ghost" size="sm" disabled={busy} data-testid="horse-price-unpublish-button">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <EyeOff className="h-3.5 w-3.5 mr-1.5" />}
                  Unpublish
                </Button>
              </ConfirmDialog>
            </>
          ) : (
            <>
              {hasListed && (
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground" title={`Rankings currently shows $ ${horse.listed_price.toLocaleString()}`}>
                  Rankings shows: <span className="font-mono tabular-nums">${" "}{horse.listed_price.toLocaleString()}</span>
                </span>
              )}
              <ConfirmDialog
                title={`Push ${horse.name}'s price to the leaderboard?`}
                description={
                  <>
                    <p>The current internal price ({formatCurrency(horse.purchase_price, "USD")}) will be copied to the public listed price and become visible to everyone on the Rankings page.</p>
                    {!isForSale && (
                      <p className="mt-2 text-xs text-[hsl(var(--ap-brass))]">
                        ⓘ This horse is currently marked <span className="font-medium">Not for sale</span>. If you want interested buyers to know it's on the market, toggle "Sale status" ON above before or after pushing.
                      </p>
                    )}
                    {hasListed && (
                      <p className="mt-2 text-xs">This will overwrite the previous listed value of <span className="font-mono">${" "}{horse.listed_price.toLocaleString()}</span> — there is no history.</p>
                    )}
                  </>
                }
                confirmLabel="Yes, push price"
                destructive={false}
                onConfirm={push}
                testId={`horse-price-push-${horse.id}`}
              >
                <Button size="sm" disabled={busy} data-testid="horse-price-push-button">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                  {hasListed ? "Re-push price" : "Push to rankings"}
                </Button>
              </ConfirmDialog>
            </>
          )}
        </div>
      )}
    </div>
  );
}




// Prev/next horse navigation strip. Renders the two arrow buttons plus
// a small "17 of 47" position badge. Registers keyboard shortcuts
// (← / → arrow keys) so power-users can flick through horses without
// touching the mouse. Text input elements are excluded so the shortcut
// doesn't hijack typing in the edit dialog.
function HorseNavStrip({ neighbors }) {
  const navigate = useNavigate();
  const { prev, next, position, total } = neighbors || {};

  React.useEffect(() => {
    const onKey = (e) => {
      // Ignore when the user is typing in a form control — otherwise
      // "→" in a text field would rip them to a different horse mid-edit.
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (e.target && e.target.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // If the full-screen photo lightbox is open, let it own the arrow
      // keys so → / ← flick between photos instead of jumping horses.
      if (document.body.hasAttribute("data-lightbox-open")) return;
      if (e.key === "ArrowLeft" && prev?.id) {
        e.preventDefault();
        navigate(`/horses/${prev.id}`);
      } else if (e.key === "ArrowRight" && next?.id) {
        e.preventDefault();
        navigate(`/horses/${next.id}`);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev?.id, next?.id, navigate]);

  return (
    <div className="flex items-center gap-1" data-testid="horse-nav-strip">
      <Button
        variant="outline"
        size="sm"
        className="h-8 px-2.5"
        disabled={!prev?.id}
        onClick={() => prev?.id && navigate(`/horses/${prev.id}`)}
        title={prev?.name ? `Previous: ${prev.name} (←)` : "No previous horse"}
        data-testid="horse-nav-prev"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        <span className="hidden sm:inline ml-1 truncate max-w-[100px]">{prev?.name || "Previous"}</span>
      </Button>
      {position != null && total != null && (
        <span className="text-[11px] text-muted-foreground tabular-nums px-2" data-testid="horse-nav-position">
          {position} / {total}
        </span>
      )}
      <Button
        variant="outline"
        size="sm"
        className="h-8 px-2.5"
        disabled={!next?.id}
        onClick={() => next?.id && navigate(`/horses/${next.id}`)}
        title={next?.name ? `Next: ${next.name} (→)` : "No next horse"}
        data-testid="horse-nav-next"
      >
        <span className="hidden sm:inline mr-1 truncate max-w-[100px]">{next?.name || "Next"}</span>
        <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}


/**
 * Per-horse AI health & workload trends: hoof-abscess photo history +
 * sessions during the current in-work stint. Fetches /trends/horse/{id}
 * (which also lazily analyses any new photos server-side and caches).
 */
function HorseTrendsSection({ horseId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/trends/horse/${horseId}`);
        if (alive) setData(res.data);
      } catch (_e) {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [horseId]);

  const rides = data?.rides;
  const history = data?.hoof_history || [];
  const flagged = history.filter((h) => h.detected);

  return (
    <Card className="rounded-xl" data-testid="horse-trends-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[hsl(var(--ap-brass))]" />
          Health &amp; workload trends
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Reading trends…
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2">
            {/* Hoof-abscess */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1.5 mb-2">
                <Stethoscope className="h-3.5 w-3.5" /> Hoof-abscess watch
              </div>
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground">No photos analysed yet.</p>
              ) : flagged.length === 0 ? (
                <p className="text-sm flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  No soaking bags detected in {history.length} analysed photo{history.length !== 1 ? "s" : ""}.
                </p>
              ) : (
                <div className="space-y-2" data-testid="horse-trends-hoof">
                  <div className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--ap-warning)/0.4)] bg-[hsl(var(--ap-warning)/0.1)] px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--ap-warning))]">
                    <AlertTriangle className="h-3 w-3" /> {flagged.length} possible abscess sign{flagged.length !== 1 ? "s" : ""}
                  </div>
                  {flagged.slice(0, 4).map((h, i) => (
                    <div key={h.photo_id || i} className="text-xs text-muted-foreground border-l-2 border-[hsl(var(--ap-warning)/0.4)] pl-2">
                      <span className="text-foreground">{h.uploaded_at ? safeFormatDate(h.uploaded_at, "MMM d, yyyy") : "Photo"}</span>
                      {typeof h.confidence === "number" && ` · ${Math.round(h.confidence * 100)}%`}
                      {h.notes && <div className="italic">{h.notes}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* In-work rides */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1.5 mb-2">
                <TrendingUp className="h-3.5 w-3.5" /> Rides this in-work stint
              </div>
              {!rides || rides.sessions_in_work == null ? (
                <p className="text-sm text-muted-foreground">Not currently in a tracked work period.</p>
              ) : (
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-serif text-3xl tabular-nums" data-testid="horse-trends-sessions">{rides.sessions_in_work}</span>
                    <span className="text-sm text-muted-foreground">sessions</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {rides.ongoing ? "Since going in work" : "During last work stint"}
                    {rides.in_work_since && ` · from ${safeFormatDate(rides.in_work_since, "MMM d, yyyy")}`}
                    {rides.approximate && " (≈ last 30 days)"}
                  </p>
                  {rides.sessions_per_week != null && (
                    <p className="text-xs text-muted-foreground mt-0.5">{rides.sessions_per_week} rides/week over {rides.window_days} days</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Per-horse rider tracking + per-season work/rest breakdown.
 * - Riders: who rode this horse, how often, avg score, and an AI-derived
 *   dedication score (auto — no manual logging; adjusted so weeks this
 *   horse was injured don't count against a rider).
 * - Seasons: this horse's work-days vs rest-days + rides per season.
 */
function HorseRidersSeasons({ horseId }) {
  const [riders, setRiders] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/riders/summary`, { params: { horse_id: horseId } }),
        api.get(`/seasons/horse/${horseId}`),
      ]);
      setRiders(r.data?.riders || []);
      setSeasons(s.data?.seasons || []);
    } catch (_e) { setRiders([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [horseId]);

  return (
    <Card className="rounded-xl" data-testid="horse-riders-seasons">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4 text-[hsl(var(--ap-forest))]" />
          Riders &amp; seasons
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {/* Riders */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Who rides this horse</div>
              {!riders || riders.length === 0 ? (
                <p className="text-sm text-muted-foreground">No rides recorded yet.</p>
              ) : (
                <div className="space-y-2" data-testid="horse-riders-list">
                  {riders.map((r) => (
                    <div key={r.rider} className="rounded-lg border p-2.5">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{r.rider}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">{r.sessions} rides{r.avg_score != null ? ` · ${r.avg_score} avg` : ""}</span>
                      </div>
                      <div className="mt-1.5 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid={`rider-dedication-${r.rider}`}>
                          <Star className="h-3 w-3 text-[hsl(var(--ap-brass))]" />
                          {r.dedication != null ? `${r.dedication_label} · ${r.dedication}` : "No rides yet"}
                        </span>
                        {r.dedication != null && (
                          <div className="h-1.5 w-20 rounded-full bg-muted overflow-hidden" aria-hidden="true">
                            <div className="h-full rounded-full bg-[hsl(var(--ap-forest))]" style={{ width: `${Math.max(4, r.dedication)}%` }} />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Seasons for this horse */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <CalendarRange className="h-3.5 w-3.5" /> This horse by season
              </div>
              {seasons.length === 0 ? (
                <p className="text-sm text-muted-foreground">No seasons defined yet.</p>
              ) : (
                <div className="space-y-2" data-testid="horse-seasons-list">
                  {seasons.map((s) => (
                    <div key={s.id} className="rounded-lg border p-2.5">
                      <div className="font-medium text-sm">{s.name}</div>
                      <div className="mt-1 grid grid-cols-3 gap-2 text-center">
                        <div><div className="font-serif text-lg tabular-nums leading-none">{s.total_rides}</div><div className="text-[9px] uppercase tracking-wider text-muted-foreground">rides</div></div>
                        <div><div className="font-serif text-lg tabular-nums leading-none">{s.work_days}</div><div className="text-[9px] uppercase tracking-wider text-muted-foreground">work days</div></div>
                        <div><div className="font-serif text-lg tabular-nums leading-none">{s.rest_days}</div><div className="text-[9px] uppercase tracking-wider text-muted-foreground">rest days</div></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

